<template>
  <div style="margin:20px">
    <div style="width: 100%; background-color: #eee; border: 1px dotted grey; margin-bottom: 25px;">
      <span style="font-size: 26px; font-weight: bold">Driver Scorecard</span><br/>
      <span style="font-style: italic; font-size: 18px">Individual driver performance metrics and KPIs from Samsara Safety, Samsara Idling, TBN Dispatch Pricing, and TBN Driver Availability</span>
    </div>

    <div class="scorecard-controls-row">
      <div class="scorecard-controls-group">
        <a href="#" class="white-link" v-on:click.prevent="shift_date_range(-1)"><</a>
        <div class="scorecard-date-picker-wrapper">
          <date-picker class="scorecard-date-picker" v-model="start_date" :config="options" :placeholder="start_date" @update:model-value="validate_date_range"/>
        </div>
        <span>to</span>
        <div class="scorecard-date-picker-wrapper">
          <date-picker class="scorecard-date-picker" v-model="end_date" :config="options" :placeholder="end_date" @update:model-value="validate_date_range"/>
        </div>
        <a href="#" class="white-link" v-on:click.prevent="shift_date_range(1)">></a>
      </div>
      <div class="scorecard-controls-group">
        <span>Driver:</span>
        <b-form-select v-if="driver_options.length > 0" v-model="selected_driver_id" :options="driver_options" class="scorecard-driver-select"/>
        <span v-else><b-spinner style="width: 2rem; height: 2rem;" label="Loading..."></b-spinner></span>
        <b-button size="sm" variant="outline-secondary" v-on:click.prevent="clear_driver_list_cache()" :disabled="clearing_cache">Clear driver list cache</b-button>
        <b-button v-on:click.prevent="load_scorecard()" :disabled="loading || !selected_driver_id">Load Scorecard</b-button>
      </div>
    </div>

    <div v-if="loading" style="text-align: center; margin: 50px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <div v-else-if="scorecard_data" style="margin: 20px;">
      <b-card>
        <div class="d-flex flex-wrap align-items-center justify-content-center gap-3 mb-2">
          <h3 class="mb-0">{{ scorecard_data.driver_name || 'Driver Scorecard' }}</h3>
          <div class="ml-4">
            <h5 class="mb-0">Miles Driven</h5>
            <h3 class="mb-0">{{ formatNumber(scorecard_data.miles_driven, 2) || formatNumber(scorecard_data.distance_traveled_miles, 2) || '0.00' }}</h3>
          </div>
        </div>
        <p>Date Range: {{ formatDate(start_date) }} to {{ formatDate(end_date) }} ({{ scorecard_data.date_range_days || 0 }} days)<span v-if="within_five_days" class="text-muted ml-2">— We are not sampling from the most recent 5 days.</span></p>
        
        <hr/>
        
        <h4>Risk Metrics</h4>
        <div class="scorecard-metrics-row">
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Risk Score</h5>
            <h2 :class="getRiskScoreClass(scorecard_data.risk_score)">
              {{ scorecard_data.risk_score || 'N/A' }}
            </h2>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Risk Rank</h5>
            <h2 :class="getRiskRankClass(scorecard_data.risk_score_rank)">
              {{ scorecard_data.risk_score_rank || 'N/A' }}
            </h2>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Speeding %</h5>
            <h3>{{ formatNumber(scorecard_data.speeding_percentage, 2) || 0 }}%</h3>
          </b-card>
          <b-card v-if="false" bg-variant="light" class="metric-card metric-card-inline">
            <h5>Light Speeding Events per 10k Miles</h5>
            <h3>{{ formatNumber(scorecard_data.light_speeding_events_per_10k_miles, 2) || 0 }}</h3>
          </b-card>
          <b-card v-if="false" bg-variant="light" class="metric-card metric-card-inline">
            <h5>Moderate Speeding Events per 10k Miles</h5>
            <h3>{{ formatNumber(scorecard_data.moderate_speeding_events_per_10k_miles, 2) || 0 }}</h3>
          </b-card>
          <b-card v-if="false" bg-variant="light" class="metric-card metric-card-inline">
            <h5>Heavy Speeding Events per 10k Miles</h5>
            <h3>{{ formatNumber(scorecard_data.heavy_speeding_events_per_10k_miles, 2) || 0 }}</h3>
          </b-card>
          <b-card v-if="false" bg-variant="light" class="metric-card metric-card-inline">
            <h5>Severe Speeding Events per 10k Miles</h5>
            <h3>{{ formatNumber(scorecard_data.severe_speeding_events_per_10k_miles, 2) || 0 }}</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Distracted Driving per 10k</h5>
            <h3>{{ formatNumber(scorecard_data.distracted_driving_percentage, 2) || 0 }}</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Following Dist. per 10k</h5>
            <h3>{{ formatNumber(scorecard_data.following_distance_per_10k_miles, 2) || 0 }}</h3>
          </b-card>
        </div>

        <hr/>

        <h4>Idling & Fuel Efficiency</h4>
        <div class="scorecard-metrics-row">
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Idling %</h5>
            <h3 :class="getIdlingClass(scorecard_data.idling_percentage)">
              {{ formatNumber(scorecard_data.idling_percentage, 2) }}%
            </h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>MPG</h5>
            <h3 :class="getMpgClass(scorecard_data.miles_per_gallon)">{{ formatNumber(scorecard_data.miles_per_gallon, 2) || 'N/A' }}</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Idle Hrs</h5>
            <h3>{{ formatNumber(scorecard_data.idle_hours, 2) }}</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Run Hrs</h5>
            <h3>{{ formatNumber(scorecard_data.run_hours, 2) }}</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Gallons</h5>
            <h3>{{ formatNumber(scorecard_data.fuel_consumed_gallons, 2) || '0.00' }}</h3>
          </b-card>
        </div>

        <hr/>

        <h4>Dispatch (from TBN)</h4>
        <div class="scorecard-metrics-row">
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Customer Requests</h5>
            <h3 :class="(scorecard_data.customer_requests || 0) === 0 ? 'text-warning' : 'text-success'">{{ scorecard_data.customer_requests || 0 }}</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Bus Days</h5>
            <h3>{{ scorecard_data.bus_days || 0 }}</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Dispatch Miles</h5>
            <h3>{{ formatNumber(scorecard_data.total_miles, 2) }}</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Weekend Avail.</h5>
            <h3>{{ formatNumber(scorecard_data.weekend_availability_percentage, 2) || 0 }}%</h3>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Cust. Req. per 10k</h5>
            <h3 :class="(scorecard_data.customer_requests_per_10k_miles || 0) === 0 ? 'text-warning' : 'text-success'">{{ formatNumber(scorecard_data.customer_requests_per_10k_miles, 2) || 0 }}</h3>
          </b-card>
        </div>
      </b-card>
    </div>

    <div v-else-if="!loading && selected_driver_id" style="text-align: center; margin: 50px; color: #666;">
      <p>No scorecard data available. Click "Load Scorecard" to fetch data.</p>
    </div>
  </div>
</template>

<script>
import {mapState} from "vuex"
import {justDate} from '../helpers/dates'
import moment from 'moment'
import {get_driver_scorecard, tbn_get_drivers_scorecard_list, tbn_clear_scorecard_driver_list_cache} from '../networking/tbn_apis'

export default {
  name: 'driver_scorecard',

  components: {
  },

  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    within_five_days() {
      if (!this.end_date) return false
      const today = moment().startOf('day')
      const end = moment(this.end_date, 'MM-DD-YYYY').startOf('day')
      const daysDiff = today.diff(end, 'days')
      return daysDiff <= 5
    }
  },

  data() {
    return {
      loading: false,
      scorecard_data: null,
      selected_driver_id: null,
      driver_options: [],
      total_drivers: 0, // Total number of drivers for the tenant
      start_date: justDate(moment().subtract(89, 'days')), // default last 90 days (avoids API timeout)
      end_date: justDate(moment()),
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment('2025-01-01', 'YYYY-MM-DD'),
        maxDate: moment(),
        keepOpen: false,
        showClear: true,
        showClose: true,
        widgetPositioning: { horizontal: 'left', vertical: 'bottom' },
      },
    }
  },

  created() {
    this.load_drivers()
  },

  watch: {
    start_date() {
      this.$nextTick(() => this.validate_date_range())
    },
    end_date() {
      this.$nextTick(() => this.validate_date_range())
    },
  },

  methods: {
    async clear_driver_list_cache() {
      if (!this.user || !this.user.tenant) return
      this.clearing_cache = true
      try {
        await tbn_clear_scorecard_driver_list_cache(this.user.tenant)
        this.$bvToast.toast('Driver list cache cleared. Reload the page to rebuild.', { variant: 'success', autoHideDelay: 4000 })
        await this.load_drivers()
      } catch (e) {
        this.$bvToast.toast((e.response && e.response.data && e.response.data.message) || 'Failed to clear cache', { variant: 'danger' })
      } finally {
        this.clearing_cache = false
      }
    },
    async load_drivers() {
      try {
        const response = await tbn_get_drivers_scorecard_list(this.user.tenant)
        const {value: {data}} = response
        
        // The API returns {status_code: 200, data: results}
        // But execute_generator yields {data: response_data} where response_data is {status_code: 200, data: results}
        // So value.data is {status_code: 200, data: results}, and we need value.data.data
        // However, based on console logs, data appears to be the array directly
        let drivers = []
        if (Array.isArray(data)) {
          // Data is already the array (unexpected but handling it)
          drivers = data
        } else if (data && data.data && Array.isArray(data.data)) {
          // Data is wrapped in {status_code, data: results} (expected case)
          drivers = data.data
        } else {
          console.warn('Unexpected data format:', data)
        }
        
        // Filter out the "All" option (value === '') - API returns only drivers with samsaraDriverName, already sorted
        const filteredDrivers = drivers.filter(driver =>
          driver.value && driver.value !== ''
        )
        // Alphabetize by display name (API also sorts; this keeps dropdown order consistent)
        const sorted = [...filteredDrivers].sort((a, b) => {
          const textA = (a.text || a.fullName || a.name || '').toLowerCase()
          const textB = (b.text || b.fullName || b.name || '').toLowerCase()
          return textA.localeCompare(textB)
        })
        
        // Store total driver count for percentile calculations
        this.total_drivers = sorted.length
        
        this.driver_options = [
          { value: null, text: 'Select Driver' },
          ...sorted.map(driver => ({
            value: driver.value, // Use TBN driver ID (scorecard API expects TBN driver ID)
            text: driver.text || driver.fullName || driver.name || `${driver.firstName || ''} ${driver.lastName || ''}`.trim()
          }))
        ]
      } catch (error) {
        console.error('Error loading drivers:', error)
        this.driver_options = [{ value: null, text: 'Select Driver' }]
      }
    },

    async load_scorecard() {
      if (!this.selected_driver_id) {
        console.log('No driver selected')
        return
      }

      // Validate date range (max 90 days)
      const start = moment(this.start_date, 'MM-DD-YYYY')
      const end = moment(this.end_date, 'MM-DD-YYYY')
      if (!start.isValid() || !end.isValid()) return
      const days_diff = end.diff(start, 'days')
      const MAX_DAYS = 90
      if (days_diff > MAX_DAYS) {
        this.$bvToast.toast(`Date range cannot exceed ${MAX_DAYS} days (${days_diff} selected). Please select a smaller range.`, {
          title: 'Date Range Too Large',
          variant: 'warning',
          solid: true,
          autoHideDelay: 5000
        })
        return
      }

      if (days_diff < 0) {
        this.$bvToast.toast('End date must be after start date', {
          title: 'Invalid Date Range',
          variant: 'warning',
          solid: true
        })
        return
      }

      console.log('=== LOADING SCORECARD ===')
      console.log('Driver ID:', this.selected_driver_id)
      console.log('Tenant:', this.user.tenant)
      console.log('Start Date:', moment(this.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD'))
      console.log('End Date:', moment(this.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD'))

      this.loading = true
      this.scorecard_data = null

      try {
        const start_date_str = moment(this.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
        const end_date_str = moment(this.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
        
        console.log('Calling API with:', {
          tenant: this.user.tenant,
          driver_id: this.selected_driver_id,
          start_date: start_date_str,
          end_date: end_date_str
        })
        
        const response = await get_driver_scorecard(
          this.user.tenant,
          this.selected_driver_id,
          start_date_str,
          end_date_str
        )

        const selectedLabel = (this.driver_options.find(d => d.value === this.selected_driver_id) || {}).text || ''
        const isFunkAaron = /funk/i.test(selectedLabel) && /aaron/i.test(selectedLabel)

        console.log('=== API RESPONSE ===')
        console.log('Full response:', response)
        if (isFunkAaron) {
          console.log('[FUNK AARON] Full API response:', JSON.stringify(response, null, 2))
        }
        console.log('Response.value:', response && response.value)
        console.log('Response.value.data:', response && response.value && response.value.data)
        console.log('Response.value.data.results:', response && response.value && response.value.data && response.value.data.results)

        // Extract data from generator response structure: {value: {data: {...}}, done: false}
        const responseData = (response && response.value && response.value.data) || (response && response.data)
        
        if (responseData) {
          if (responseData.error) {
            this.$bvToast.toast(responseData.error, {
              title: 'Scorecard Error',
              variant: 'warning',
              solid: true,
              autoHideDelay: 8000
            })
          } else if (responseData.results) {
            console.log('Scorecard data found:', responseData.results)
            if (isFunkAaron) {
              console.log('[FUNK AARON] Full scorecard results:', JSON.stringify(responseData.results, null, 2))
            }
            console.log('Risk score:', responseData.results.risk_score)
            console.log('Miles driven:', responseData.results.miles_driven)
            console.log('Idling percentage:', responseData.results.idling_percentage)
            console.log('Customer requests:', responseData.results.customer_requests)
            console.log('Total revenue:', responseData.results.total_revenue)
            this.scorecard_data = responseData.results
          } else {
            console.warn('No results in response data:', responseData)
            this.$bvToast.toast('No scorecard data available for this driver and date range', {
              title: 'No Data',
              variant: 'warning',
              solid: true
            })
          }
        } else {
          console.warn('Unexpected response structure:', response)
          this.$bvToast.toast('Unexpected response format from server', {
            title: 'Error',
            variant: 'danger',
            solid: true
          })
        }
      } catch (error) {
        console.error('=== ERROR LOADING SCORECARD ===', error)
        const serverMsg = (error.response && error.response.data && error.response.data.error) || (error.response && error.response.data && error.response.data.data && error.response.data.data.error)
        const message = serverMsg || error.message || 'Error loading scorecard data'
        this.$bvToast.toast(message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 8000
        })
      } finally {
        this.loading = false
        console.log('=== LOADING COMPLETE ===')
      }
    },

    validate_date_range() {
      // Cap date range at 90 days
      const MAX_DAYS = 90
      const start = moment(this.start_date, 'MM-DD-YYYY')
      const end = moment(this.end_date, 'MM-DD-YYYY')
      if (!start.isValid() || !end.isValid()) return
      const days_diff = end.diff(start, 'days')
      if (days_diff < 0) {
        this.start_date = this.end_date
        return
      }
      if (days_diff > MAX_DAYS) {
        const adjusted_end = start.clone().add(MAX_DAYS, 'days')
        this.end_date = justDate(adjusted_end)
        this.$bvToast.toast(`Date range limited to ${MAX_DAYS} days. End date adjusted to ${adjusted_end.format('MM/DD/YYYY')}.`, {
          title: 'Date Range Limited',
          variant: 'info',
          solid: true,
          autoHideDelay: 4000
        })
      }
    },

    shift_date_range(days) {
      const start = moment(this.start_date, 'MM-DD-YYYY')
      let end = moment(this.end_date, 'MM-DD-YYYY')
      if (!start.isValid() || !end.isValid()) return
      start.add(days, 'days')
      end.add(days, 'days')
      // Ensure we don't exceed max date
      const today = moment().startOf('day')
      if (end.isAfter(today)) end = today.clone()
      this.start_date = justDate(start)
      this.end_date = justDate(end)
      this.validate_date_range()
    },

    formatDate(date) {
      if (!date) return ''
      // Handle both MM-DD-YYYY string format and Date objects
      const parsed = moment(date, 'MM-DD-YYYY', true)
      return parsed.isValid() ? parsed.format('MM/DD/YYYY') : moment(date).format('MM/DD/YYYY')
    },

    formatNumber(value, decimals = 2) {
      if (value === null || value === undefined) {
        return '0.00'
      }
      return Number(value).toFixed(decimals)
    },

    getRiskScoreClass(riskScore) {
      if (riskScore === null || riskScore === undefined) {
        return ''
      }
      const score = Number(riskScore)
      if (score >= 80) {
        return 'text-success' // Green for good scores (80-100)
      } else if (score >= 50) {
        return 'text-warning' // Yellow/orange for moderate scores (50-79)
      } else {
        return 'text-danger' // Red for bad scores (<50)
      }
    },

    getRiskRankClass(riskRank) {
      if (riskRank === null || riskRank === undefined || this.total_drivers === 0) {
        return ''
      }
      const rank = Number(riskRank)
      const totalDrivers = this.total_drivers
      
      // Calculate percentile (lower rank is better, rank 1 is best)
      // Percentile = (rank / total_drivers) * 100
      const percentile = (rank / totalDrivers) * 100
      
      // Top 25% = green (best drivers)
      // Bottom 25% = red (worst drivers)
      // Middle 50% = yellow
      if (percentile <= 25) {
        return 'text-success' // Green for top 25% (best drivers)
      } else if (percentile <= 75) {
        return 'text-warning' // Yellow for middle 50%
      } else {
        return 'text-danger' // Red for bottom 25% (worst drivers)
      }
    },

    getIdlingClass(idlingPercentage) {
      if (idlingPercentage === null || idlingPercentage === undefined) {
        return ''
      }
      const percentage = Number(idlingPercentage)
      const average = 21.8 // Average idling percentage
      if (percentage < average) {
        return 'text-success' // Green for below average (good)
      } else if (percentage <= 30) {
        return 'text-warning' // Yellow for around average (21.8-30%)
      } else {
        return 'text-danger' // Red for above 30% (bad)
      }
    },

    getMpgClass(mpg) {
      if (mpg === null || mpg === undefined) {
        return 'text-danger'
      }
      const value = Number(mpg)
      if (value >= 7.1) {
        return 'text-success' // Green: 7.1 or above
      }
      if (value >= 6.6) {
        return 'text-warning' // Yellow: 7.0 down to 6.6
      }
      return 'text-danger' // Red: below 6.6
    }
  }
}
</script>

<style scoped>
.white-link {
  color: white;
  text-decoration: none;
  font-size: 24px;
}
.white-link:hover {
  color: #ccc;
}

/* Responsive date picker and driver select */
.date-picker-responsive {
  font-size: 20px;
  font-weight: bold;
  width: 140px;
  min-width: 120px;
}

.driver-select-responsive {
  font-size: 20px;
  font-weight: bold;
  width: 220px;
  min-width: 180px;
}

.btn-responsive {
  white-space: nowrap;
}

/* Responsive adjustments for smaller screens */
@media (max-width: 768px) {
  .date-picker-responsive {
    font-size: 16px;
    width: 120px;
    min-width: 100px;
  }
  
  .driver-select-responsive {
    font-size: 16px;
    width: 180px;
    min-width: 150px;
  }
  
  .metric-card {
    padding: 0.75rem;
  }
  
  .metric-card h5 {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }
  
  .metric-card h2 {
    font-size: 1.5rem;
  }
  
  .metric-card h3 {
    font-size: 1.25rem;
  }
}

@media (max-width: 576px) {
  .date-picker-responsive {
    font-size: 14px;
    width: 100px;
    min-width: 90px;
  }
  
  .driver-select-responsive {
    font-size: 14px;
    width: 150px;
    min-width: 120px;
  }
  
  .metric-card {
    padding: 0.5rem;
  }
  
  .metric-card h5 {
    font-size: 0.85rem;
    margin-bottom: 0.4rem;
  }
  
  .metric-card h2 {
    font-size: 1.25rem;
  }
  
  .metric-card h3 {
    font-size: 1.1rem;
  }
}

/* Single-line metrics row: flex nowrap, inline cards; start-aligned so leftmost card is visible when scrolling */
.scorecard-metrics-row {
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  gap: 1.1rem;
  margin-bottom: 1.35rem;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 8px;
  -webkit-overflow-scrolling: touch;
}
.metric-card-inline {
  flex: 0 0 auto;
  min-width: 195px;
  padding: 1.1rem 1.25rem !important;
  margin-bottom: 0 !important;
}
.metric-card-inline h5 {
  font-size: 1.1rem;
  margin-bottom: 0.4rem;
  line-height: 1.2;
}
.metric-card-inline h2 {
  font-size: 1.85rem;
}
.metric-card-inline h3 {
  font-size: 1.6rem;
}

/* Top controls: wrap on small screens so date and Load Scorecard stay on page; center when wrapped */
.scorecard-controls-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  font-weight: bold;
  font-size: 1.1rem;
  margin-bottom: 1rem;
}
.scorecard-controls-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}
/* Wrapper keeps datetimepicker dropdown next to its input (prevents flex from pushing it to bottom of page) */
.scorecard-date-picker-wrapper {
  position: relative;
  overflow: visible;
  align-self: flex-start;
}
.scorecard-date-picker {
  font-size: 1rem;
  font-weight: bold;
  width: 140px;
  min-width: 0;
  max-width: 100%;
}
.scorecard-driver-select {
  font-size: 1rem;
  font-weight: bold;
  width: 220px;
  min-width: 0;
  max-width: 100%;
}
@media (max-width: 768px) {
  .scorecard-controls-row {
    font-size: 1rem;
    gap: 0.75rem;
  }
  .scorecard-date-picker {
    width: 120px;
    font-size: 0.95rem;
  }
  .scorecard-driver-select {
    width: 180px;
    font-size: 0.95rem;
  }
}

/* Gap utility for flex containers */
.gap-2 {
  gap: 0.5rem;
}
</style>

